import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { SiteManagementService } from '../site-management.service';
import { BuildingManagementService } from 'src/app/building-managment/building-management.service';
import { SiteManagerComponent } from '../site-manager/site-manager.component';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { PageName } from 'src/app/shared/utils/user-roles';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { SiteForm } from 'src/app/shared/models/data-interfaces';
import { Site } from '../site-management-interfaces';
import { Utility } from 'src/app/shared/utils/utility.service';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { ConfirmDialogComponent } from 'src/app/shared-layout/Components/confirm-dialog/confirm-dialog.component';
import { ERROR_CODES } from 'src/app/shared/constants/error-codes';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { TIME_ZONES } from 'src/app/shared/constants/timezones';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { CommonService } from 'src/app/shared/utils/common.service';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { IAppState } from 'src/app/shared/state-management/state-interfaces';
import { NgRedux } from '@angular-redux/store';
import { userTypeResponse } from 'src/app/shared/constants/response-mapping';
import { TranslateService } from '@ngx-translate/core';

type FileUploadModel = {
  data: File;
  state: string;
  inProgress: boolean;
  progress: number;
  canRetry: boolean;
  canCancel: boolean;
};

@Component({
  selector: 'app-edit-site',
  templateUrl: './edit-site.component.html',
  styleUrls: ['./edit-site.component.scss'],
})
export class EditSiteComponent implements OnInit, OnDestroy {
  PageName = PageName;
  CONSTANTS = CONSTANTS;
  site: any;
  buildings: any;
  formGroup: any;
  file: any;
  sitePhotoKey: string | undefined;
  sitePhotoURL: string | undefined;
  public ngDestroyed$ = new Subject();
  sharedData: any;
  subscription: Subscription | undefined;
  editBuildingMode = false;
  readonly LANG_CONST = LANG_CONST;
  previousSiteData!: SiteForm;
  @Input() public selectedSite: any;
  @Input() navigateToAddBuilding: any;
  @Input() navigateConfirmDialog: any;
  @Input() closeConfirmDialog: any;
  @Input() noErrorToasterShow = false;
  readonly TIME_ZONES = TIME_ZONES;
  filteredSuggestions: Observable<string[]> | undefined;
  userType: string = '';

  constructor(
    public dialogRef: MatDialogRef<SiteManagerComponent>,
    private siteManagementService: SiteManagementService,
    private buildingManagementService: BuildingManagementService,
    private sharedDataService: SharedDataService,
    private helperService: HelperService,
    private notificationService: NotificationService,
    private commonService: CommonService,
    private ngRedux: NgRedux<IAppState>,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      name: new FormControl(
        null,
        MFValidators.compose([
          MFValidators.required,
          MFValidators.minLength(1),
          MFValidators.maxLength(50),
          MFValidators.whitespace,
          MFValidators.entityNameV2,
        ])
      ),
      address: new FormControl(
        null,
        MFValidators.compose([
          MFValidators.required,
          MFValidators.minLength(2),
          MFValidators.maxLength(255),
          MFValidators.whitespace,
          MFValidators.addressPattern,
        ])
      ),
      siteZone: new FormControl({ value: '', disabled: true }),
    });

    this.subscription = this.sharedDataService.currentData.subscribe(
      (sharedData) => {
        this.sharedData = sharedData;
      }
    );

    /**Getting selected site information and updating the form ui */
    this.siteManagementService.getAllSites().subscribe({
      next: (sites: Site) => {
        this.site =
          sites &&
          sites.find((site: any) => site.siteId == this.selectedSite?.siteId);
        this.formGroup.patchValue({
          name: this.site?.siteName,
          address: this.site?.address,
          photo: this.site?.siteImgName || '',
          siteZone: this.sharedData?.selectedSite?.siteTimeZone || '',
        });
        this.sitePhotoURL = this.site?.siteImgUrl;
        this.sitePhotoKey = this.site?.sitePicture;
        this.previousSiteData = {
          siteId: this.site?.siteId,
          siteName: this.site?.siteName,
          address: this.site?.address,
        };
      },
      error: (err: Error) => {
        this.notificationService.showError(err?.message);
      },
    });

    /**Getting buildings list */
    this.buildingManagementService
      .getAllBuildingsBySiteId(
        this.selectedSite?.siteId,
        true,
        this.noErrorToasterShow
      )
      .subscribe({
        next: (res: any) => {
          this.buildings = res;
        },
        error: (err: Error) => {
          this.notificationService.showError(err?.message);
        },
      });

    this.filteredSuggestions = this.formGroup.get('address').valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: string) =>
        value.length > 0
          ? this.siteManagementService.getAddressSuggestions(value, true)
          : of([])
      )
    );
    this.ngRedux?.select('user')?.subscribe((res: any) => {
      this.userType = res?.userType;
      if (res?.userType === userTypeResponse[1]) {
        return;
      } else {
        this.formGroup.disable();
      }
    });
  }

  openAddBuilding() {
    this.userType === CONSTANTS.PROPERTY_MANAGER_ADMIN
      ? this.onUpdateSiteHandler(false)
      : null;
    this.sharedDataService.setEditMode({
      building: false,
      floor: false,
    });
    this.navigateToAddBuilding();
  }

  editBuilding(buildingId: any, buildingName: any) {
    const navigateToEdit = () => {
      this.sharedDataService.setSelectedBuilding({
        buildingId,
        buildingName,
      });
      this.sharedDataService.setEditMode({
        building: true,
        floor: false,
      });
      this.navigateToAddBuilding();
    };

    if (this.userType === CONSTANTS.PROPERTY_MANAGER_ADMIN) {
      this.onUpdateSiteHandler(false, navigateToEdit);
    } else {
      navigateToEdit();
    }
  }

  closeDialog() {
    let siteId = this.site?.siteId;
    let siteName = this.formGroup?.controls?.name?.value;
    let address = this.formGroup?.controls?.address?.value;
    let siteImgName = this.formGroup?.controls?.photo?.value;

    if (!this.isDataChanged(siteId, siteName, address)) {
      this.sharedDataService.setEditMode({
        building: false,
        floor: false,
      });
      this.dialogRef.close();
      return;
    }

    this.navigateConfirmDialog();
    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      this.closeConfirmDialog();
      if (result) {
        this.sharedDataService.setEditMode({
          building: false,
          floor: false,
        });
        this.dialogRef.close();
      }
    });
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next(void 0);
    this.ngDestroyed$.complete();
  }

  addPhotoClickHandler() {
    const fileUpload: any = document.getElementById(
      'fileUpload'
    ) as HTMLInputElement;
    fileUpload.onchange = () => {
      const file = fileUpload?.files[0];

      this.formGroup.patchValue({
        photo: file?.name,
      });

      if (!Utility.checkValidImageExtension(file?.name || 'file.invalid')) {
        this.formGroup.controls.photo.setErrors({ invalidImage: true });
        return;
      }

      this.file = {
        data: file,
        state: 'in',
        inProgress: false,
        progress: 0,
        canRetry: false,
        canCancel: true,
      };
      this.startUploadProcess(file);
    };
    fileUpload.click();
  }

  startUploadProcess(file: File) {
    if (Utility.checkValidImageSize(file?.size, 25)) {
      this.formGroup?.controls?.photo?.setErrors({ invalidImageSize: true });
      return;
    }
    const fileType = file?.type;
    this.siteManagementService.getSiteBucketPresignedURL(fileType).subscribe({
      next: (res: any) => {
        this.uploadFile(res, file);
      },
      error: (error: Error) => {},
    });
  }

  uploadFile(preSignedURLData: any, file: File) {
    let { fields } = preSignedURLData;
    this.sitePhotoKey = fields['key'];
    var fd = new FormData();
    let fieldsKeys = Object.keys(fields);
    for (const field of fieldsKeys) {
      fd.append(field, fields[field]);
    }
    fd.append('file', file);
    // this call post image to the S3 bucket with presignedUrl
    this.siteManagementService.uploadFile(preSignedURLData.url, fd).subscribe(
      (response: any) => {},
      (error: Error) => {}
    );
  }

  onUpdateSiteHandler(isDone: boolean, callback?: () => void) {
    let siteId = this.site?.siteId;
    let siteName = this.formGroup?.controls?.name?.value;
    let address = this.formGroup?.controls?.address?.value;
    if (!this.isDataChanged(siteId, siteName, address)) {
      if (isDone) this.dialogRef.close();
      if (callback) callback();
      return;
    }

    this.siteManagementService
      .updateSiteDetails(siteId, siteName, address)
      .subscribe({
        next: (res: any) => {
          if (res && res.siteName)
            this.sharedDataService.setSelectedSite({
              siteId: res.siteId,
              siteName: res.siteName,
              siteTimeZone: this.formGroup?.controls?.siteZone?.value,
              commonAccessControl: res.commonAccessControl,
            });
          if (isDone) this.dialogRef.close();
          if (callback) callback();
          this.notificationService.showSuccess(
            this.translate.instant('SUCCESSFULLY_UPDATED')
          );
        },
        error: (err: Error) => {
          this.notificationService.showError(err?.message);
        },
      });
  }

  photoInputClick() {
    const fileDownloader: any = document.getElementById(
      'site-photo-download'
    ) as HTMLInputElement;
    fileDownloader.click();
  }

  isDataChanged(siteId: any, siteName: any, address: any) {
    if (
      this.previousSiteData.siteId === siteId &&
      this.previousSiteData.siteName === siteName &&
      this.previousSiteData.address === address
    )
      return false;
    else return true;
  }
}
